import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { RuleConditions } from "@unbox/api";
import { handleError } from "@/api/error";
import { invalidateRulesDashboard } from "@/api/rules";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

// ── Query key builders ────────────────────────────────────────────────────────

const blocklistRulesKey = (merchantId: string, workspaceId: string | null | undefined) =>
  ["blocklist-rules", merchantId, workspaceId] as const;

const blocklistExemptionsKey = (merchantId: string, workspaceId: string | null | undefined) =>
  ["blocklist-exemptions", merchantId, workspaceId] as const;

// ── Invalidation ───────────────────────────────────────────────────────────────

const invalidateRules = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ["blocklist-rules"] });
  queryClient.invalidateQueries({ queryKey: ["blocklist-rules-count"] });
  queryClient.invalidateQueries({ queryKey: ["blocked-creators"] });
  queryClient.invalidateQueries({ queryKey: ["blocked-creators-count"] });
  invalidateRulesDashboard(queryClient);
};

// ── Rules CRUD ─────────────────────────────────────────────────────────────────

export const useBlocklistRules = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: blocklistRulesKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.rules.$get({ param: scope.param })
        : await client.merchant[":merchantId"].blocklist.rules.$get({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useCreateBlocklistRule = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, conditions }: { name: string; conditions: RuleConditions }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.rules.$post({
            param: scope.param,
            json: { name, conditions },
          })
        : await client.merchant[":merchantId"].blocklist.rules.$post({
            param: scope.param,
            json: { name, conditions },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateRules(queryClient),
  });
};

export const useUpdateBlocklistRule = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ruleId,
      ...patch
    }: {
      ruleId: string;
      name?: string;
      conditions?: RuleConditions;
      isActive?: boolean;
      unblockAlreadyBlocked?: boolean;
    }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.rules[":ruleId"].$patch({
            param: { ...scope.param, ruleId },
            json: patch,
          })
        : await client.merchant[":merchantId"].blocklist.rules[":ruleId"].$patch({
            param: { ...scope.param, ruleId },
            json: patch,
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateRules(queryClient),
  });
};

export const useDeleteBlocklistRule = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleId: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.rules[":ruleId"].$delete({
            param: { ...scope.param, ruleId },
          })
        : await client.merchant[":merchantId"].blocklist.rules[":ruleId"].$delete({
            param: { ...scope.param, ruleId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateRules(queryClient),
  });
};

// ── Impact preview (draft, not persisted) ───────────────────────────────────────

export const useRulePreview = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (conditions: RuleConditions) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.rules.preview.$post({
            param: scope.param,
            json: conditions,
          })
        : await client.merchant[":merchantId"].blocklist.rules.preview.$post({
            param: scope.param,
            json: conditions,
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
  });
};

// ── Exemptions ("individually exempt" creators, exempt from all rules) ─────────

export const useBlocklistExemptions = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: blocklistExemptionsKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.exemptions.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].blocklist.exemptions.$get({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useRemoveExemption = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorOpenId: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.exemptions[":creatorOpenId"].$delete(
            {
              param: { ...scope.param, creatorOpenId },
            },
          )
        : await client.merchant[":merchantId"].blocklist.exemptions[":creatorOpenId"].$delete({
            param: { ...scope.param, creatorOpenId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blocklist-exemptions"] });
      invalidateRules(queryClient);
    },
  });
};
